import React, { useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { formatNumberToK } from '@/shared/utils';
import { InstagramQueryPostItemModel } from '@/types/anonymously.interface';
import dynamic from 'next/dynamic';
const ImageGallery = dynamic(() => import('@/components/Common/ImageGallery'), {
  ssr: false,
});
const LocalImage = dynamic(() => import('@/components/Common/LocalImage'), {
  ssr: false,
});

interface PostsAnalysisProps {
  post_list?: InstagramQueryPostItemModel[];
  analyzeAll?: boolean;
  locked?: boolean;
  gotoPlanClick?: () => void;
}

const PostsAnalysis: React.FC<PostsAnalysisProps> = ({
  post_list,
  analyzeAll = false,
  locked = false,
  gotoPlanClick,
}) => {
  const { t } = useTranslation();
  const [showGallery, setShowGallery] = useState(false);
  const [currentPostIndex, setCurrentPostIndex] = useState(0);

  const postsAnalysis = useMemo(() => {
    if (!post_list || post_list.length === 0) return null;

    // 使用首次加载的12条数据或全部数据
    const dataToAnalyze = analyzeAll ? post_list : post_list.slice(0, 12);

    if (dataToAnalyze.length === 0) return null;

    // 找出点赞最多的帖子
    const mostPopularIndex = dataToAnalyze.reduce((maxIndex, post, index, array) => {
      return (post.like_count || 0) > (array[maxIndex].like_count || 0) ? index : maxIndex;
    }, 0);

    const mostPopular = dataToAnalyze[mostPopularIndex];

    // 在完整列表中找到这个帖子的原始索引
    let originalIndex = mostPopularIndex;
    if (analyzeAll) {
      // 尝试在完整列表中找到相同的帖子
      // 使用帖子 code 进行比较，因为这是最可靠的标识符
      const postCode = mostPopular.code;
      const foundIndex = post_list.findIndex((post) => post.code === postCode);

      if (foundIndex >= 0) {
        originalIndex = foundIndex;
      }
    }

    // 计算平均点赞数
    const totalLikes = dataToAnalyze.reduce((sum, post) => sum + (post.like_count || 0), 0);
    const avgLikes = Math.round(totalLikes / dataToAnalyze.length);

    // 计算平均评论数
    const totalComments = dataToAnalyze.reduce((sum, post) => sum + (post.comment_count || 0), 0);
    const avgComments = Math.round(totalComments / dataToAnalyze.length);

    return {
      mostPopular,
      mostPopularIndex: originalIndex,
      avgLikes,
      avgComments,
    };
  }, [post_list, analyzeAll]);

  // 处理点击分析区域的帖子
  const handlePostClick = (index: number) => {
    // 如果帖子被锁定，则不支持点击
    if (locked) {
      return;
    }

    setCurrentPostIndex(index);
    setShowGallery(true);
  };

  if (!postsAnalysis) return null;

  return (
    <div className="mb-6 rounded-lg overflow-hidden shadow-sm border border-gray-100">
      <div className="px-6 py-5 flex max-md:flex-col max-md:gap-6 max-md:px-3 max-md:py-3">
        {/* 最受欢迎帖子 */}
        <div className="flex-1 flex items-center gap-8 max-md:gap-4">
          {/* 最受欢迎帖子 - 图片 */}
          <div className="flex items-center justify-center">
            <div
              className="w-[140px] h-[140px] relative rounded-md overflow-hidden cursor-pointer"
              onClick={() => handlePostClick(postsAnalysis.mostPopularIndex)}
            >
              <LocalImage
                src={postsAnalysis.mostPopular?.cover_image || postsAnalysis.mostPopular?.thumbnail_url}
                alt={
                  typeof postsAnalysis.mostPopular?.caption === 'string'
                    ? postsAnalysis.mostPopular?.caption
                    : postsAnalysis.mostPopular?.caption?.text
                }
                style={{ width: '140px', height: '140px', objectFit: 'cover' }}
              />
              {postsAnalysis.mostPopular?.is_video && (
                <div className="absolute top-2 right-2">
                  <Image alt="" src="/images/toolkit/video-small-icon.webp" width={16} height={16} />
                </div>
              )}
            </div>
          </div>

          {/* 最受欢迎帖子 - 数据 */}
          <div className="">
            <div className="text-[20px] leading-6 mb-6 max-md:text-[16px]">{t('Most Popular Recently')}</div>
            <div className="flex gap-20 max-md:flex-col max-md:gap-0">
              <div>
                <div className="text-[24px] leading-7 font-medium">
                  {formatNumberToK(postsAnalysis.mostPopular?.like_count || 0)}
                </div>
                <div className="text-sm text-gray-500">{t('likes')}</div>
              </div>
              <div>
                <div className="text-[24px] leading-7 font-medium">
                  {formatNumberToK(postsAnalysis.mostPopular?.comment_count || 0)}
                </div>
                <div className="text-sm text-gray-500">{t('comments')}</div>
              </div>
            </div>
          </div>
        </div>

        {/* 平均数据 */}
        <div className="flex-1 flex flex-col justify-center">
          <div className="text-[20px] leading-6 mb-6 max-md:text-[16px]">{t('Average')}</div>
          <div className="flex gap-20">
            <div>
              <div className="mb-1 text-[24px] leading-7 font-medium">{formatNumberToK(postsAnalysis.avgLikes)}</div>
              <div className="text-sm text-gray-500">{t('Avg.likes')}</div>
            </div>
            <div>
              <div className="mb-1 text-[24px] leading-7 font-medium">{formatNumberToK(postsAnalysis.avgComments)}</div>
              <div className="text-sm text-gray-500">{t('Avg.comments')}</div>
            </div>
          </div>
        </div>
      </div>
      {showGallery && (
        <ImageGallery
          dataList={post_list}
          index={currentPostIndex}
          show={showGallery}
          onClose={() => setShowGallery(false)}
          gotoPlanClick={gotoPlanClick}
        />
      )}
    </div>
  );
};

export default PostsAnalysis;
